import {Injectable} from '@angular/core';
import {AngularFireAuth} from '@angular/fire/compat/auth';
import {AngularFirestore} from '@angular/fire/compat/firestore';
import {Observable, from, of} from 'rxjs';
import {catchError, map, shareReplay, switchMap} from 'rxjs/operators';
import { normalizeAppRole, normalizeAppRoles } from './role-utils';

export interface TenantContext {
  uid: string | null;
  orgId: string | null;
  facilityIds: string[];
  primaryFacilityId: string | null;
  roles: string[];
  displayName: string | null;
  role: string | null;
  credentials: string | null;
  npi: string | null;
  supervisorUid: string | null;
  supervisorName: string | null;
}

@Injectable({providedIn: 'root'})
export class TenantContextService {
  readonly context$: Observable<TenantContext> = this.afAuth.authState.pipe(
    switchMap((user) => {
      if (!user?.uid) {
        return of(this.emptyContext());
      }

      return this.afs.doc(`users/${user.uid}`).valueChanges().pipe(
        switchMap((doc) => from(user.getIdTokenResult()).pipe(
          map((token) => this.buildContext(
            user.uid,
            (doc as Record<string, unknown> | undefined) ?? {},
            (token?.claims as Record<string, unknown> | undefined) ?? {}
          )),
          catchError((tokenError) => {
            console.warn('TenantContextService: signed tenant claim unavailable; using profile tenant only.', tokenError);
            return of(this.buildContext(
              user.uid,
              (doc as Record<string, unknown> | undefined) ?? {},
              {}
            ));
          })
        )),
        catchError((error) => {
          console.warn('TenantContextService: users profile read denied.', error);
          return from(user.getIdTokenResult()).pipe(
            map((token) => this.buildContext(
              user.uid,
              {},
              (token?.claims as Record<string, unknown> | undefined) ?? {}
            )),
            catchError((tokenError) => {
              console.warn('TenantContextService: signed tenant claim fallback unavailable.', tokenError);
              return of(this.buildContext(user.uid, {}, {}));
            })
          );
        })
      );
    }),
    shareReplay({bufferSize: 1, refCount: true})
  );

  constructor(
    private readonly afAuth: AngularFireAuth,
    private readonly afs: AngularFirestore
  ) {}

  private emptyContext(): TenantContext {
    return {
      uid: null,
      orgId: null,
      facilityIds: [],
      primaryFacilityId: null,
      roles: [],
      displayName: null,
      role: null,
      credentials: null,
      npi: null,
      supervisorUid: null,
      supervisorName: null,
    };
  }

  private buildContext(
    uid: string,
    profile: Record<string, unknown>,
    claims: Record<string, unknown>
  ): TenantContext {
    const rawRole = this.readString(profile, 'role');
    const normalizedRole = normalizeAppRole(rawRole);
    // super_admin is never trusted from the mutable Firestore profile.
    // Platform authority exists only in PrivilegedClaimsService.
    const role = normalizedRole === 'super_admin' ? null : normalizedRole;
    const rawRoles = this.readStringArray(profile, 'roles');
    const roles = normalizeAppRoles(rawRoles.length ? rawRoles : (role ? [role] : []))
      .filter((value) => value !== 'super_admin');
    const facilityIds = this.readStringArray(profile, 'facilityIds');
    const primaryFacilityId =
      this.readString(profile, 'primaryFacilityId', 'facilityId', 'facilityID') ??
      facilityIds[0] ??
      null;

    return {
      uid,
      // Operational roles and facility assignment stay profile-only. The
      // organization may fall back to the signed ID-token claim because the
      // admin guard and Firestore same-org rules already use that trusted
      // claim, including for legacy admin accounts without a users/{uid}
      // profile. Never merge claim roles into this clinical context.
      orgId:
        this.readString(profile, 'orgId', 'orgID', 'tenantId', 'tenantID') ??
        this.readString(claims, 'orgId', 'orgID', 'tenantId', 'tenantID'),
      facilityIds,
      primaryFacilityId,
      roles,
      displayName: this.readString(profile, 'displayName'),
      role,
      credentials: this.readString(profile, 'credentials'),
      npi: this.readString(profile, 'npi'),
      supervisorUid: this.readString(profile, 'supervisorUid'),
      supervisorName: this.readString(profile, 'supervisorName'),
    };
  }

  private readString(source: Record<string, unknown>, ...keys: string[]): string | null {
    for (const key of keys) {
      const value = source[key];
      if (typeof value === 'string' && value.trim()) {
        return value;
      }
    }
    return null;
  }

  private readStringArray(source: Record<string, unknown>, key: string): string[] {
    const value = source[key];
    if (Array.isArray(value)) {
      return value.filter((item): item is string => typeof item === 'string' && item.trim().length > 0);
    }

    const single = this.readString(source, key);
    return single ? [single] : [];
  }
}
